/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Controller, type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SearchAutocomplete, type SearchAutocompleteOption } from '@/shared/ui/components/form/SearchAutocomplete';
import { Select } from '@/shared/ui/components/form/inputs/Select';
import { useClientAttendances } from '../../hooks/useClientAttendances';
import type { VisitFormData } from './schema';

interface PropertyClientSectionProps {
  control: Control<VisitFormData>;
  agencyId: string;
  clientId: string;
  propertyOptions: SearchAutocompleteOption[];
  clientOptions: SearchAutocompleteOption[];
}

/**
 * Property and client search fields for the visit drawer (the section title
 * owns the labels, so the inputs use placeholders only).
 *
 * @param props - The form control and the property/client autocomplete options.
 * @returns The property and client search section.
 */
export function PropertyClientSection({
  control,
  agencyId,
  clientId,
  propertyOptions,
  clientOptions,
}: PropertyClientSectionProps) {
  const { t } = useTranslation('features/visits');
  const { options: attendanceOptions } = useClientAttendances(agencyId, clientId);
  return (
    <div className="flex flex-col gap-(--space-4)">
      <Controller
        name="propertyId"
        control={control}
        render={({ field }) => (
          <SearchAutocomplete
            placeholder={t('drawer.searchProperty')}
            options={propertyOptions}
            value={field.value || null}
            onChange={(val) => field.onChange(val ?? '')}
          />
        )}
      />
      <Controller
        name="clientId"
        control={control}
        render={({ field }) => (
          <SearchAutocomplete
            placeholder={t('drawer.searchClient')}
            options={clientOptions}
            value={field.value || null}
            onChange={(val) => field.onChange(val ?? '')}
          />
        )}
      />
      <Controller
        name="dealId"
        control={control}
        render={({ field }) => (
          <Select
            name="dealId"
            value={field.value}
            onChange={(val) => field.onChange(String(val ?? ''))}
            disabled={!clientId}
            placeholder={t('drawer.attendance')}
            data={[
              { value: '', label: t('drawer.newAttendance') },
              ...attendanceOptions,
            ]}
          />
        )}
      />
    </div>
  );
}
